import React from 'react';
import {
  MOBILE_MENU_FOOTER,
  MOBILE_MENU_MAIN_ORDER,
  getDrawerTitle,
  DrawerType,
  type ResolvedMenuItem,
  type ResolvedMenuDrawerConfig,
  type ResolvedDrawerItem,
} from '../../MenuConfig';
import { GroupIdEnum } from 'app/modules/Segment/types';
import { ROUTES_PATH } from 'app/constants/Routes';
import type { ProfileMenuAccountItem } from '../ProfileMenu';
import {
  MGM_INVITE_ACTION_KEY,
  type FlattenedMenuEntry,
  type MobileMenuFooterItem,
  type PushItemParams,
} from '../../types';

const DRAWER_AS_SECTION_TITLE: DrawerType[] = [
  DrawerType.PROCESSOS,
  DrawerType.CLIENTES,
  DrawerType.CONFIGURACOES,
  DrawerType.JUSFYPAY,
];

/** Título da "Gestão da conta" — o mesmo do menu de perfil do sidebar web. */
export const ACADEMY_ACCOUNT_SECTION_TITLE = 'Gestão da conta';

/**
 * Aluno (plano academy): no celular ele vê o MESMO menu do sidebar web — os N1
 * dele, o perfil com a "Gestão da conta" e o rodapé. Nada das gavetas do
 * advogado (Configurações, Processos…).
 */
export type AcademyMobileMenu = {
  /** O "Perfil" do sidebar (o avatar): vira o item que abre a tela de perfil. */
  profileMenuItem: ResolvedMenuItem | null;
  /** Itens da "Gestão da conta" do menu de perfil, já filtrados pelas rotas do aluno. */
  accountItems: ProfileMenuAccountItem[];
};

function isDrawerSectionTitle(item: ResolvedMenuItem): boolean {
  return Boolean(
    item.drawerType && DRAWER_AS_SECTION_TITLE.includes(item.drawerType),
  );
}

function resolveMenuGroupId(
  trackEvent?: ResolvedMenuItem['trackEvent'],
): GroupIdEnum {
  return trackEvent?.groupId ?? GroupIdEnum.GENERAL_GROUP;
}

function isExternalMenuPath(path: string, externalLink?: boolean): boolean {
  return (
    Boolean(externalLink) ||
    path.startsWith('http://') ||
    path.startsWith('https://')
  );
}

function shouldSkipDrawerSubItem(sub: ResolvedDrawerItem): boolean {
  return sub.show === false;
}

function toDrawerSubItemParams(
  sub: ResolvedDrawerItem,
  drawerTitle: string,
): PushItemParams {
  const path = typeof sub.path === 'string' ? sub.path : '#';

  return {
    icon: sub.icon,
    label: sub.label,
    path,
    groupId: resolveMenuGroupId(sub.trackEvent),
    externalLink: isExternalMenuPath(path, sub.externalLink),
    badge: sub.badge,
    categoryParent: drawerTitle,
    disabled: sub.disabled,
    disabledTooltip: sub.disabledTooltip,
  };
}

export function getFlattenedMenuItems(params: {
  mainItems: ResolvedMenuItem[];
  settingsItems: ResolvedMenuItem[];
  drawerConfig: ResolvedMenuDrawerConfig;
  gamificationMenuItem: ResolvedMenuItem | null;
  gamificationMobileLabel: string;
  gamificationMobileIcon: React.ReactNode;
  showMgmInviteFooterAction?: boolean;
  /** Presente para o aluno: monta o menu dele em vez do menu do advogado. */
  academy?: AcademyMobileMenu;
}): FlattenedMenuEntry[] {
  const {
    mainItems,
    settingsItems,
    drawerConfig,
    gamificationMenuItem,
    gamificationMobileLabel,
    gamificationMobileIcon,
    showMgmInviteFooterAction = false,
    academy,
  } = params;
  const result: FlattenedMenuEntry[] = [];
  let index = 0;

  const pushItem = ({
    icon,
    label,
    groupId,
    path,
    actionKey,
    externalLink,
    iconPosition,
    iconSize,
    iconColor,
    badge,
    categoryParent,
    showIcon,
    disabled,
    disabledTooltip,
  }: PushItemParams) => {
    result.push({
      type: 'item',
      id: `mobile-${index++}`,
      icon,
      label,
      groupId,
      path,
      actionKey,
      externalLink,
      iconPosition,
      iconSize,
      iconColor,
      badge,
      categoryParent,
      showIcon,
      disabled,
      disabledTooltip,
    });
  };

  const pushSectionTitle = (label: string) => {
    result.push({
      type: 'sectionTitle',
      id: `mobile-section-${index++}`,
      label,
    });
  };

  const pushDivider = () => {
    result.push({
      type: 'divider',
      id: `mobile-divider-${index++}`,
    });
  };

  const appendTopLevelItem = (item: ResolvedMenuItem) => {
    if (isDrawerSectionTitle(item)) {
      pushSectionTitle(item.label);
      return;
    }

    pushItem({
      icon: item.icon,
      label: item.label,
      path: item.route,
      groupId: resolveMenuGroupId(item.trackEvent),
      iconSize: 16,
      badge: item.drawerType ? undefined : item.badge,
      disabled: item.disabled,
      disabledTooltip: item.disabledTooltip,
    });
  };

  const appendDrawerSubItems = (
    item: ResolvedMenuItem,
    config: ResolvedMenuDrawerConfig,
  ) => {
    if (!item.drawerType) return;

    const drawerTitle = getDrawerTitle(item.drawerType);
    const sections = config[item.drawerType] ?? [];

    for (const section of sections) {
      for (const sub of section.items) {
        const resolvedSub = sub as ResolvedDrawerItem;
        if (shouldSkipDrawerSubItem(resolvedSub)) continue;

        pushItem(toDrawerSubItemParams(resolvedSub, drawerTitle));
      }
    }
  };

  const processItems = (
    items: ResolvedMenuItem[],
    config: ResolvedMenuDrawerConfig,
  ) => {
    for (const item of items) {
      if (item.show === false) continue;

      appendTopLevelItem(item);
      appendDrawerSubItems(item, config);
    }
  };

  /* O menu do aluno no celular é o do sidebar web dele: os N1 na ordem da tela
     (o useResolvedMenu já os entrega assim, sem gaveta — MOBILE_MENU_MAIN_ORDER
     é a ordem do advogado), depois o perfil e a "Gestão da conta", como no menu
     de perfil. Antes o aluno via, além dos seus itens fora de ordem, a seção
     Configurações inteira do advogado (Usuários, Agenda, Consultas em lote…). */
  const appendAcademyEntries = ({
    profileMenuItem,
    accountItems,
  }: AcademyMobileMenu) => {
    processItems(mainItems, drawerConfig);
    pushDivider();
    if (profileMenuItem && profileMenuItem.show !== false) {
      pushItem({
        icon: profileMenuItem.icon,
        label: profileMenuItem.label,
        path: ROUTES_PATH.SETTINGS.PERFIL,
        groupId: resolveMenuGroupId(profileMenuItem.trackEvent),
        iconSize: 16,
      });
    }
    if (accountItems.length > 0) {
      pushSectionTitle(ACADEMY_ACCOUNT_SECTION_TITLE);
      for (const item of accountItems) {
        pushItem({
          icon: item.icon,
          label: item.label,
          path: item.path,
          groupId: GroupIdEnum.GENERAL_GROUP,
          categoryParent: ACADEMY_ACCOUNT_SECTION_TITLE,
          disabled: item.disabled,
          disabledTooltip: item.disabledTooltip,
        });
      }
    }
  };

  if (academy) {
    appendAcademyEntries(academy);
  } else {
    const orderedMainItems = MOBILE_MENU_MAIN_ORDER.map((id) =>
      mainItems.find((i) => i.id === id),
    ).filter((item): item is ResolvedMenuItem => item != null);

    processItems(orderedMainItems, drawerConfig);
    processItems(settingsItems, drawerConfig);
    if (gamificationMenuItem && gamificationMenuItem.show !== false) {
      pushDivider();
      pushItem({
        icon: gamificationMobileIcon,
        label: gamificationMobileLabel,
        path: ROUTES_PATH.GAMIFICATION.BASE,
        groupId: resolveMenuGroupId(gamificationMenuItem.trackEvent),
        badge: gamificationMenuItem.badge,
      });
    }
  }

  const pushMobileFooterEntry = (entry: MobileMenuFooterItem) => {
    switch (entry.type) {
      case 'action':
        if (
          entry.actionKey === MGM_INVITE_ACTION_KEY &&
          !showMgmInviteFooterAction
        ) {
          return;
        }
        pushItem({
          icon: null,
          label: entry.label,
          actionKey: entry.actionKey,
          groupId: GroupIdEnum.GENERAL_GROUP,
          categoryParent: null,
        });
        return;
      case 'item': {
        if (entry.externalLink) {
          pushItem({
            icon: entry.icon,
            label: entry.label,
            path: entry.path,
            groupId: GroupIdEnum.GENERAL_GROUP,
            externalLink: true,
            categoryParent: null,
          });
          return;
        }
        pushItem({
          icon: entry.icon,
          label: entry.label,
          path: entry.path,
          groupId: GroupIdEnum.GENERAL_GROUP,
          iconPosition: 'right',
          iconSize: 16,
          categoryParent: null,
          ...(entry.hideIcon && { showIcon: false }),
        });
      }
    }
  };

  pushDivider();
  for (const entry of MOBILE_MENU_FOOTER) {
    // O menu de perfil do sidebar web não tem "Sobre a Jusfy": o aluno fica igual.
    if (academy && entry.type === 'item' && entry.id === 'sobre') continue;
    pushMobileFooterEntry(entry);
  }

  return result;
}
